import React, { useEffect, useRef, useState } from "react";
import {
  useTheme,
  CssBaseline,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { baseapi } from "../Environmentalurl";
import { useSelector } from "react-redux";
import {
  ApplicationType,
  date,
  entity_id,
  location,
  locationGeo,
  orderID,
  Platform,
} from "../Utils/Const";
import { useNavigate } from "react-router-dom";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function DTH() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const md = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // ── Operator / field state ───────────────────────────────────────────────
  const [operator, setOperator] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorData, setSelectedOperatorData] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});
  const [selectedField, setSelectedField] = useState("");

  // ── Bill fetch state ─────────────────────────────────────────────────────
  const [billDetails, setBillDetails] = useState(null);
  const [billFetchOpen, setBillFetchOpen] = useState(false);
  const [openFetchDialog, setOpenFetchDialog] = useState(false);
  const [preparedPaymentPayload, setPreparedPaymentPayload] = useState(null);

  // ── TPIN ─────────────────────────────────────────────────────────────────
  const [tpinOpen, setTpinOpen] = useState(false);
  const [tpin, setTpin] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);

  // ── Insufficient balance ──────────────────────────────────────────────────
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientMsg, setInsufficientMsg] = useState("");

  // ── Charges summary (Bill → GetCharges → show summary → TPIN) ────────────
  const [chargesLoading, setChargesLoading] = useState(false);
  const [chargesData, setChargesData] = useState(null);
  const [chargesOpen, setChargesOpen] = useState(false);

  // ── Payment result dialog ─────────────────────────────────────────────────
  const [paymentResult, setPaymentResult] = useState({
    open: false,
    success: false,
    message: "",
  });

  const ip = useIPAddress();
  const bbpsUrls = useSelector((state) => state.auth.bbpsUrls);
  const DthURL = bbpsUrls?.DTH;
  const mobile = JSON.parse(
    sessionStorage.getItem("mobileNumber") || "null"
  );
  const session = JSON.parse(sessionStorage.getItem("SessionID"));

  // ── Fetch operators ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        const response = await api.post(`${baseapi}/getbbpsservices`, {
          url: DthURL,
        });
        if (Array.isArray(response.data)) {
          setOperators(response.data);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDynamicChange = (param, value) => {
    setDynamicValues((prev) => ({ ...prev, [param]: value }));
  };

  const isTataPlay = selectedOperatorData
    ? selectedOperatorData.Text?.toLowerCase().includes("tata")
    : false;

  // ── Build Optional1/Optional2 ─────────────────────────────────────────────
  const buildOptionalParams = () => {
    if (selectedOperatorData?.Value === "BHAR00000NATB4") {
      const filled = dynamicFields.filter(
        (f) => dynamicValues[f.paramName] && dynamicValues[f.paramName].trim() !== "",
      );
      return {
        Optional1: filled.map((f) => f.paramName),
        Optional2: filled.map((f) => dynamicValues[f.paramName]),
      };
    }

    if (isTataPlay) {
      if (!selectedField) { alert("Please select identifier"); return null; }
      const val = dynamicValues[selectedField];
      if (!val || val.trim() === "") { alert(`Please enter value for ${selectedField}`); return null; }
      return { Optional1: [selectedField], Optional2: [val] };
    }

    const filled = dynamicFields.filter(
      (f) => dynamicValues[f.paramName] && dynamicValues[f.paramName].trim() !== "",
    );
    return {
      Optional1: filled.map((f) => f.paramName),
      Optional2: filled.map((f) => dynamicValues[f.paramName]),
    };
  };

  // ── Step 1: Fetch Bill (BillFetchEnabled operators) ───────────────────────
  const handleFetchBill = async () => {
    if (!operator) return alert("Select operator");
    setPreparedPaymentPayload(null);
    const opt = buildOptionalParams();
    if (!opt) return;
    const { Optional1, Optional2 } = opt;
    const accountValue = Optional2.length ? Optional2[0] : "";

    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: mobile,
      SessionID: session,
      Date: date,
      OrderId: orderID,
      TPIN: "",
      RequestType: "BA_BBPS_Billfetch",
      Account_num: accountValue,
      SpKey: operator,
      Amount: "1",
      Optional1,
      Optional2,
      Optional3: "true",
      Optional4: "",
      Optional5: "",
      Optional6: "",
      Optional7: "",
      Statement: "NOT_SUPPORTED",
      Narration: "",
      IsBillFetch: "",
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: locationGeo,
      ServiceID: "3",
      ParameterInfo: "",
    };

    try {
      const billFetchRes = await api.post(`${baseapi}/JanAadhaareWalletBBPSPayments`, payload);

      if (billFetchRes.data.Response_Code === "1000") {
        setBillDetails({
          billAmount: billFetchRes.data.billAmount,
          billerResponseInfo: billFetchRes.data.billerResponseInfo,
          orderId: billFetchRes.data.OrderId,
          customerName: billFetchRes.data.customerName,
          dueDate: billFetchRes.data.dueDate,
          Optional1,
          Optional2,
        });
        setBillFetchOpen(true);
      } else {
        alert(billFetchRes.data.Message);
      }
    } catch (error) {
      console.error("Bill fetch error:", error);
      alert("Failed to fetch bill. Please try again.");
    }
  };

  // ── Step 1b: Bill Details "Quick Pay" → Get Charges ───────────────────────
  const handlePayNowFromBill = async () => {
    setBillFetchOpen(false);
    setChargesLoading(true);
    try {
      const chargesRes = await api.post(`${baseapi}/getbbpscharges`, {
        ReqType: "TA_BBPS_GetCharges",
        TrxnAmount: String(billDetails?.billAmount || "0"),
        BillerID: selectedOperatorData?.Value || operator,
        EntityID: entity_id,
        MachineID: ip,
        ApplicationType,
        Platform,
        GeoLocation: locationGeo,
        SessionID: session,
        Mobilenumber: mobile,
        ServiceID: "3",
      });
      const resData = chargesRes.data;
      if (resData?.Response_Code !== "1000" || !resData?.TotalPayable) {
        alert(resData?.Message || "Unable to calculate charges. Please try again.");
        return;
      }
      setChargesData(resData);
      setChargesOpen(true);
    } catch {
      alert("Unable to calculate charges. Please try again.");
    } finally {
      setChargesLoading(false);
    }
  };

  // ── Step 2: Charges confirmed → check wallet balance ──────────────────────
  const handleConfirmCharges = () => {
    setChargesOpen(false);
    validateBalanceAndProceed();
  };

  // ── Balance check (runs after GetCharges, before TPIN) ────────────────────
  const validateBalanceAndProceed = async () => {
    try {
      const requestBody = {
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        Mobilenumber: mobile,
        Platform,
        WalletTypeID: "1",
        ReqType: "Jan_Aadhaar_eWallet_Statement",
        SessionID: session,
        MachineID: ip,
        FromDate: "",
        ToDate: "",
      };

      const response = await api.post(
        `${baseapi}/JanAadhaareWalletStatement`,
        requestBody,
      );

      const { Info } = response.data;
      if (!Info || Info.length === 0) {
        alert("Unable to read wallet info.");
        return;
      }

      const walletBalance = parseFloat(Info[0].Balance);
      const rechargeAmount = parseFloat(
        chargesData?.TotalPayable ??
          preparedPaymentPayload?.Amount ??
          billDetails?.billAmount ??
          amount ??
          0,
      );

      if (walletBalance < rechargeAmount) {
        setInsufficientMsg(
          `Insufficient wallet balance. Amount payable is ₹${rechargeAmount}, but your wallet balance is ₹${walletBalance}.`,
        );
        setInsufficientOpen(true);
        return;
      }

      setTpin("");
      setTpinOpen(true);
    } catch (error) {
      alert("Unable to check wallet balance!");
    }
  };

  // ── Step 3: TPIN confirmed → fire payment (BillFetch path) ──────────────
  const processFinalBillPayment = async () => {
    // ⚠️ Send the RAW base amount here, not chargesData.TotalPayable.
    // The payment API applies its own charges/GST on top of whatever
    // Amount it receives — sending it the already-charges-inclusive
    // TotalPayable causes it to add charges a second time, over-debiting
    // the wallet. TotalPayable is only for display and the balance check.
    const payableAmount = billDetails?.billAmount;

    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: mobile,
      SessionID: session,
      Date: date,
      OrderId: billDetails?.orderId,
      TPIN: tpin,
      RequestType: "BA_BBPS_PAYMENT",
      ParameterInfo: "DTH Bill Payment",
      IsExternalTransaction: "1",
      Account_num: billDetails?.Optional2?.[0] || mobile,
      CustomerID: billDetails?.Optional2?.[0] || mobile,
      SpKey: operator,
      Amount: payableAmount,
      Optional1: billDetails?.Optional1,
      Optional2: billDetails?.Optional2,
      Optional3: "true",
      Optional4: billDetails?.billerResponseInfo,
      Optional6: "NOT_SUPPORTED",
      Optional7: "",
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: location,
      Statement: "ProcessDebit",
      ServiceID: "3",
      Narration: `DTH payment of Rs.${payableAmount}`,
    };

    setIsVerifying(true);
    try {
      const payRes = await api.post(`${baseapi}/JanAadhaareWalletBBPSPayments`, payload);
      setTpinOpen(false);
      if (payRes.data.Response_Code === "1000") {
        setPaymentResult({ open: true, success: true, message: "" });
      } else if (
        payRes.data.Response_Code === "2001" ||
        payRes.data.Message?.includes("Insufficient")
      ) {
        setInsufficientMsg(payRes.data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
      } else {
        setPaymentResult({ open: true, success: false, message: payRes.data.Message || "Payment failed. Please try again." });
      }
    } catch (error) {
      console.error("Payment error:", error);
      setTpinOpen(false);
      setPaymentResult({ open: true, success: false, message: "Server error during payment. Please try again." });
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Quick Pay flow (BillFetchEnabled === false) ───────────────────────────
  // handleFetchOrPay is used for Quick Pay bill fetch (from openFetchDialog path)
  const handleFetchOrPay = async () => {
    if (!operator) { alert("Please select a DTH Operator!"); return; }
    setPreparedPaymentPayload(null);
    const board = operators.find((b) => b.Value === operator);
    if (!board) { alert("Invalid operator selected."); return; }

    const Mobilenumber = JSON.parse(sessionStorage.getItem("mobileNumber") || "null");
    const SessionID = JSON.parse(sessionStorage.getItem("SessionID") || "null");
    if (!Mobilenumber || !SessionID) { alert("Session expired. Please login again."); return; }

    const opt = buildOptionalParams();
    if (!opt) return;
    const { Optional1, Optional2 } = opt;
    const accountValue = Optional2.length ? Optional2[0] : "";

    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber,
      SessionID,
      Date: date,
      OrderId: orderID,
      TPIN: "",
      RequestType: "BA_NoNBBPS_Payment",
      Account_num: accountValue,
      CustomerID: accountValue,
      SpKey: board.Value,
      Amount: "1",
      Optional1,
      Optional2,
      Optional3: "true",
      Optional4: "",
      Optional5: "",
      Optional6: "",
      Optional7: "",
      Statement: "NOT_SUPPORTED",
      Narration: "",
      IsBillFetch: "",
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: location,
      ServiceID: "3",
      ParameterInfo: "",
    };

    try {
      const fetchOrPayRes = await api.post(`${baseapi}/JanAadhaareWalletBBPSPayments`, payload);
      setBillDetails(fetchOrPayRes.data);
      if (fetchOrPayRes.data.Response_Code === "2001" || fetchOrPayRes.data.Message?.includes("Insufficient")) {
        setInsufficientMsg(fetchOrPayRes.data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
        return;
      }
      if (fetchOrPayRes.data.Response_Code === "1000") {
        sessionStorage.setItem("orderID", fetchOrPayRes.data.OrderId);
        sessionStorage.setItem("billFetchData", JSON.stringify({
          Mobilenumber, SessionID,
          orderID: fetchOrPayRes.data.OrderId,
          Account_num: accountValue,
          CustomerID: accountValue,
          SpKey: board.Value,
          Optional1, Optional2,
          billerResponseInfo: fetchOrPayRes.data.billerResponseInfo,
          billAmount: fetchOrPayRes.data.billAmount,
          billerName: fetchOrPayRes.data.billerName,
          customerName: fetchOrPayRes.data.customerName,
          dueDate: fetchOrPayRes.data.dueDate,
        }));
        setOpenFetchDialog(true);
      } else {
        alert(fetchOrPayRes.data.Message || "Failed to fetch bill");
      }
    } catch (error) {
      console.error("DTH bill fetch error:", error);
      alert("Failed to fetch bill. Please try again.");
    }
  };

  // ── openFetchDialog "Pay Now" → get charges ───────────────────────────────
  const handleBillPayment = async () => {
    setOpenFetchDialog(false);
    setChargesLoading(true);
    const storedData = JSON.parse(sessionStorage.getItem("billFetchData") || "{}");
    try {
      const chargesRes = await api.post(`${baseapi}/getbbpscharges`, {
        ReqType: "TA_BBPS_GetCharges",
        TrxnAmount: String(storedData.billAmount || "0"),
        BillerID: selectedOperatorData?.Value || operator,
        EntityID: entity_id,
        MachineID: ip,
        ApplicationType,
        Platform,
        GeoLocation: locationGeo,
        SessionID: session,
        Mobilenumber: mobile,
        ServiceID: "3",
      });
      const resData = chargesRes.data;
      if (resData?.Response_Code !== "1000" || !resData?.TotalPayable) {
        alert(resData?.Message || "Unable to calculate charges. Please try again.");
        return;
      }
      setChargesData(resData);
      setChargesOpen(true);
    } catch {
      alert("Unable to calculate charges. Please try again.");
    } finally {
      setChargesLoading(false);
    }
  };

  // ── Quick Pay direct TPIN submit ──────────────────────────────────────────
  const handleSubmitTPIN = async () => {
    const storedData = JSON.parse(sessionStorage.getItem("billFetchData") || "{}");
    const storedOrderId = sessionStorage.getItem("orderID");

    if (!storedData?.Mobilenumber || !storedOrderId) {
      alert("No bill data found. Please fetch bill again.");
      return;
    }

    setIsVerifying(true);
    // ⚠️ Send the RAW base amount, not chargesData.TotalPayable — see
    // note in processFinalBillPayment above.
    const payableAmount = storedData.billAmount;
    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: storedData.Mobilenumber,
      SessionID: storedData.SessionID,
      Date: date,
      OrderId: storedOrderId,
      TPIN: tpin,
      RequestType: "BA_BBPS_PAYMENT",
      ParameterInfo: "DTH Bill Payment",
      IsExternalTransaction: "1",
      Account_num: storedData.Account_num,
      CustomerID: storedData.CustomerID,
      SpKey: storedData.SpKey,
      Amount: payableAmount,
      Optional1: storedData.Optional1,
      Optional2: storedData.Optional2,
      Optional3: "true",
      Optional4: storedData.billerResponseInfo,
      Optional6: "NOT_SUPPORTED",
      Optional7: "",
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: location,
      Statement: "ProcessDebit",
      ServiceID: "3",
      Narration: `DTH payment of Rs.${payableAmount}`,
    };

    try {
      const submitTpinRes = await api.post(`${baseapi}/JanAadhaareWalletBBPSPayments`, payload);
      setTpinOpen(false);
      if (submitTpinRes.data.Response_Code === "1000") {
        setPaymentResult({ open: true, success: true, message: "" });
      } else if (submitTpinRes.data.Response_Code === "2001" || submitTpinRes.data.Message?.includes("Insufficient")) {
        setInsufficientMsg(submitTpinRes.data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
      } else {
        setPaymentResult({ open: true, success: false, message: submitTpinRes.data.Message || "Payment failed. Please try again." });
      }
    } catch (err) {
      console.error("TPIN submit error:", err);
      setTpinOpen(false);
      setPaymentResult({ open: true, success: false, message: "Payment processing failed. Please try again." });
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Quick Pay (BillFetchEnabled false) → Get Charges first ────────────────
  const handleQuickPay = async () => {
    if (!amount || Number(amount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    const opt = buildOptionalParams();
    if (!opt) return;
    const { Optional1, Optional2 } = opt;

    const prepared = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: mobile,
      SessionID: session,
      Date: date,
      OrderId: orderID,
      TPIN: "",
      RequestType: "BA_NoNBBPS_Payment",
      Account_num: mobile,
      CustomerID: mobile,
      SpKey: operator,
      Amount: amount,
      Optional1,
      Optional2,
      Optional3: "true",
      Optional4: "",
      Optional6: "NOT_SUPPORTED",
      Statement: "ProcessDebit",
      Narration: `Payment of Rs. ${amount}`,
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: location,
      ServiceID: "3",
      ParameterInfo: "Quick Pay",
      IsExternalTransaction: "1",
    };

    setPreparedPaymentPayload(prepared);
    setChargesLoading(true);
    try {
      const chargesRes = await api.post(`${baseapi}/getbbpscharges`, {
        ReqType: "TA_BBPS_GetCharges",
        TrxnAmount: String(amount),
        BillerID: selectedOperatorData?.Value || operator,
        EntityID: entity_id,
        MachineID: ip,
        ApplicationType,
        Platform,
        GeoLocation: locationGeo,
        SessionID: session,
        Mobilenumber: mobile,
        ServiceID: "3",
      });
      const resData = chargesRes.data;
      if (resData?.Response_Code !== "1000" || !resData?.TotalPayable) {
        alert(resData?.Message || "Unable to calculate charges. Please try again.");
        return;
      }
      setChargesData(resData);
      setChargesOpen(true);
    } catch {
      alert("Unable to calculate charges. Please try again.");
    } finally {
      setChargesLoading(false);
    }
  };

  // ── Quick Pay: TPIN confirmed → fire payment using charges total ─────────
  const processQuickPayment = async () => {
    if (!preparedPaymentPayload) {
      alert("Payment data missing. Please try again.");
      return;
    }

    // ⚠️ Send the RAW base amount (preparedPaymentPayload.Amount, set from
    // the user-entered amount in handleQuickPay), not chargesData.TotalPayable —
    // see note in processFinalBillPayment above.
    const finalAmount = preparedPaymentPayload.Amount;

    const payload = {
      ...preparedPaymentPayload,
      TPIN: tpin,
      Amount: finalAmount,
      Narration: `Payment of Rs. ${finalAmount}`,
    };

    setIsVerifying(true);
    try {
      const quickPayRes = await api.post(`${baseapi}/JanAadhaareWalletBBPSPayments`, payload);
      setTpinOpen(false);
      if (quickPayRes.data.Response_Code === "1000") {
        setPaymentResult({ open: true, success: true, message: "" });
      } else if (
        quickPayRes.data.Response_Code === "2001" ||
        quickPayRes.data.Message?.includes("Insufficient")
      ) {
        setInsufficientMsg(quickPayRes.data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
      } else {
        setPaymentResult({ open: true, success: false, message: quickPayRes.data.Message || "Payment failed. Please try again." });
      }
    } catch (error) {
      console.error("Quick pay error:", error);
      setTpinOpen(false);
      setPaymentResult({ open: true, success: false, message: "Server error during payment. Please try again." });
    } finally {
      setIsVerifying(false);
      setPreparedPaymentPayload(null);
    }
  };

  // ── TPIN dialog confirm handler (routes to correct payment flow) ─────────
  const handleTpinConfirm = () => {
    if (preparedPaymentPayload) {
      processQuickPayment();
    } else if (billDetails?.orderId) {
      processFinalBillPayment();
    } else {
      handleSubmitTPIN();
    }
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ background: "#f8fafc", px: { xs: 2, md: 6 }, transition: "background 0.4s ease" }}>
        <Grid container spacing={3}>
          {/* Left Card */}
          <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
            <Card elevation={isDark ? 0 : 2} sx={{ background: "white", borderRadius: 3, p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.04)", border: "1px solid #f0f0f0" }}>
              <CardContent>
                <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 1, fontSize: { xs: 15 } }}>
                  DTH
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: { md: 2 }, mt: { md: 2 } }}>
                  Select your DTH service provider
                </Typography>

                <FormControl fullWidth variant="filled" sx={{ mt: 1, mb: 2 }}>
                  <InputLabel>Select your dth service</InputLabel>
                  <Select
                    value={operator}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOperator(val);
                      const opData = operators.find((o) => o.Value === val);
                      setSelectedOperatorData(opData || null);
                      setSelectedField("");
                      if (opData?.BBPSData) {
                        const fields = JSON.parse(opData.BBPSData);
                        setDynamicFields(fields);
                        const initial = {};
                        fields.forEach((f) => { initial[f.paramName] = ""; });
                        setDynamicValues(initial);
                      } else {
                        setDynamicFields([]);
                        setDynamicValues({});
                      }
                    }}
                    sx={{
                      borderRadius: 1, backgroundColor: "#f8fafc",
                      border: isDark ? "#12303a" : "1px solid #999",
                      "& .MuiFilledInput-root": { backgroundColor: "#f8fafc", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 },
                    }}
                  >
                    <MenuItem value="">Select dth Operator</MenuItem>
                    {operators.map((op, index) => (
                      <MenuItem key={index} value={op.Value}>{op.Text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {operator && selectedOperatorData && (
                  <>
                    {isTataPlay ? (
                      <>
                        <Typography variant="caption" color="text.secondary">Select Identifier</Typography>
                        <FormControl fullWidth variant="filled" sx={{ mt: 1, mb: 2 }}>
                          <InputLabel>Mobile Number / Subscriber Number</InputLabel>
                          <Select value={selectedField} onChange={(e) => setSelectedField(e.target.value)}>
                            {dynamicFields.map((field, index) => (
                              <MenuItem key={index} value={field.paramName}>{field.paramName}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {selectedField && (
                          <>
                            <Typography variant="caption" color="text.secondary">{selectedField}</Typography>
                            <TextField
                              fullWidth placeholder={selectedField} autoComplete="off" variant="filled"
                              value={dynamicValues[selectedField] || ""}
                              onChange={(e) => handleDynamicChange(selectedField, e.target.value)}
                              sx={{ mt: 1, mb: 2, backgroundColor: "#f8fafc", borderRadius: 1, "& .MuiFilledInput-root": { backgroundColor: isDark ? "#12303a" : "#f0f0f0", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 } }}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {dynamicFields.map((field, index) => (
                          <React.Fragment key={index}>
                            <Typography variant="caption" color="text.secondary">{field.paramName}</Typography>
                            <TextField
                              fullWidth autoComplete="off" placeholder={field.paramName} variant="filled"
                              value={dynamicValues[field.paramName] || ""}
                              onChange={(e) => handleDynamicChange(field.paramName, e.target.value)}
                              sx={{ mt: 1, mb: 2, backgroundColor: "#f8fafc", borderRadius: 1, "& .MuiFilledInput-root": { backgroundColor: isDark ? "#12303a" : "#f0f0f0", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 } }}
                            />
                          </React.Fragment>
                        ))}
                      </>
                    )}

                    {selectedOperatorData?.BillFetchEnabled === false && (
                      <>
                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                        <TextField
                          fullWidth placeholder="Enter amount" variant="filled" autoComplete="off"
                          value={amount} onChange={(e) => setAmount(e.target.value)}
                          sx={{ mt: 1, mb: 3, backgroundColor: "#f8fafc", borderRadius: 1, "& .MuiFilledInput-root": { backgroundColor: "#f8fafc", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 } }}
                        />
                      </>
                    )}

                    <Button
                      fullWidth size="large" endIcon={<ArrowForwardIcon />}
                      disabled={chargesLoading}
                      onClick={() => {
                        if (selectedOperatorData?.BillFetchEnabled) {
                          handleFetchBill();
                        } else {
                          handleQuickPay();
                        }
                      }}
                      sx={{ background: "linear-gradient(135deg, #16a34a, #22c55e)", color: "#fff", borderRadius: 2, py: 1.2, fontWeight: 700, textTransform: "none" }}
                    >
                      {selectedOperatorData?.BillFetchEnabled
                        ? "Fetch Bill"
                        : chargesLoading
                          ? "Calculating charges..."
                          : "Quick Pay"}
                    </Button>
                  </>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3, color: "text.secondary" }}>
                  <ShieldOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2">100% Secure & Instant Recharge</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Card */}
          {!isMobile && (
            <>
              <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
                <Card elevation={isDark ? 0 : 2} sx={{ background: "white", borderRadius: 3, p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.04)", height: "100%", border: "1px solid #f0f0f0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: isDark ? "rgba(35,190,165,0.12)" : "rgba(0,123,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: theme.palette.primary.main, mr: 1 }}>
                        <SearchIcon />
                      </Box>
                      <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>Browse Plans</Typography>
                    </Box>
                    <Box sx={{ background: isDark ? "#12303a" : "rgba(0,0,0,0.05)", borderRadius: 2, p: 3, color: "text.secondary", mt: 1, mb: 2, minHeight: 92, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography>No plans available.</Typography>
                    </Box>
                    <Box sx={{ background: isDark ? "#0f3a35" : "rgba(0,123,255,0.05)", borderRadius: 1.5, p: 1.2, color: "text.secondary", mt: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>Disclaimer:</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>While we support most recharges, please verify with your operator before proceeding.</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12 }} item xs={12}>
                <Box sx={{ borderRadius: md ? 2 : 0, p: 1.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary">Trusted by <b>UIDAI BBPS Network</b></Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* ── Bill Details Dialog (BillFetch path) ──────────────────────────── */}
      <Dialog open={billFetchOpen} onClose={() => setBillFetchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>Bill Details</DialogTitle>
        <DialogContent dividers>
          {billDetails ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Amount: ₹{billDetails.billAmount}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>Customer: <b>{billDetails.customerName}</b></Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Due Date: {billDetails.dueDate}</Typography>
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained" fullWidth size="large"
            onClick={handlePayNowFromBill}
            disabled={chargesLoading}
          >
            {chargesLoading ? "Calculating charges..." : "Pay Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── openFetchDialog (Quick Pay bill fetch result) ─────────────────── */}
      <Dialog open={openFetchDialog} onClose={() => setOpenFetchDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>Bill Details</DialogTitle>
        <DialogContent dividers>
          {billDetails ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Amount: ₹{billDetails.billAmount || billDetails.billerName}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {billDetails.customerName}
              </Typography>
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained" fullWidth size="large"
            onClick={handleBillPayment}
            disabled={chargesLoading}
          >
            {chargesLoading ? "Calculating charges..." : "Pay Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TPIN Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={tpinOpen} onClose={() => setTpinOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>🔐 Enter TPIN</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            {[0, 1, 2, 3].map((index) => (
              <TextField
                key={index} type="password"
                value={tpin[index] || ""}
                inputRef={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(-1);
                  const newTpin = tpin.padEnd(4, "").split("");
                  newTpin[index] = value;
                  setTpin(newTpin.join(""));
                  if (value && index < 3) inputRefs.current[index + 1]?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !tpin[index] && index > 0) inputRefs.current[index - 1]?.focus();
                }}
                inputProps={{ maxLength: 1, style: { textAlign: "center", fontSize: "22px", fontWeight: 600 } }}
                sx={{ width: 60, "& .MuiOutlinedInput-root": { height: 60, borderRadius: 3 } }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTpinOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained" fullWidth
            disabled={tpin.length !== 4 || isVerifying}
            onClick={handleTpinConfirm}
          >
            {isVerifying ? "Verifying..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Insufficient Balance Dialog ─────────────────────────────────────── */}
      <Dialog open={insufficientOpen} onClose={() => setInsufficientOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1, width: 380 } }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 600, fontSize: 18, pb: 0.5 }}>⚠️ Insufficient Balance</DialogTitle>
        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{insufficientMsg}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInsufficientOpen(false)} fullWidth variant="outlined" sx={{ borderRadius: 2, textTransform: "none", fontWeight: 500 }}>Cancel</Button>
          <Button fullWidth variant="contained" onClick={() => { setInsufficientOpen(false); navigate("/dashboard/loadmoney"); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, ml: 1, color: "white" }}>Load Money</Button>
        </DialogActions>
      </Dialog>

      {/* ── CHARGES SUMMARY DIALOG ──────────────────────────────────────────── */}
      <Dialog open={chargesOpen} onClose={() => setChargesOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.18)" } }}>
        <Box sx={{ background: "linear-gradient(90deg, #007BFF 0%, #00C9A7 100%)", py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>Payment Summary</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>Review charges before proceeding</Typography>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {chargesData && (
            <Box sx={{ bgcolor: "#f5f7fb", borderRadius: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1.2}>
                <Typography color="text.secondary" fontSize={14}>Transaction Amount</Typography>
                <Typography fontWeight={600}>₹{chargesData.TransactionAmount}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1.2}>
                <Typography color="text.secondary" fontSize={14}>Charges</Typography>
                <Typography fontWeight={600}>₹{chargesData.Charges}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1.2} pb={1.5} sx={{ borderBottom: "1px dashed #ccc" }}>
                <Typography color="text.secondary" fontSize={14}>GST</Typography>
                <Typography fontWeight={600}>₹{chargesData.GST}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1.5}>
                <Typography fontWeight={700} fontSize={16}>Total Payable</Typography>
                <Typography fontWeight={700} fontSize={16} sx={{ color: "#007BFF" }}>₹{chargesData.TotalPayable}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setChargesOpen(false)} sx={{ textTransform: "none", borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" fullWidth size="large" onClick={handleConfirmCharges}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: "linear-gradient(90deg, #007BFF 0%, #00C9A7 100%)", boxShadow: "0 6px 16px rgba(0,123,255,0.3)" }}>
            Pay Now ₹{chargesData?.TotalPayable}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PAYMENT RESULT DIALOG ───────────────────────────────────────────── */}
      <Dialog open={paymentResult.open} onClose={() => setPaymentResult({ ...paymentResult, open: false })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.18)" } }}>
        <Box sx={{ background: paymentResult.success ? "linear-gradient(135deg, #16a34a, #22c55e)" : "linear-gradient(135deg, #dc2626, #b91c1c)", py: 3.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 40 }}>{paymentResult.success ? "🎉" : "❌"}</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{paymentResult.success ? "Payment Successful!" : "Payment Failed"}</Typography>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.6 }}>
            {paymentResult.success ? "Your DTH bill has been paid successfully." : paymentResult.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button variant="contained" fullWidth size="large"
            onClick={() => { setPaymentResult({ ...paymentResult, open: false }); if (paymentResult.success) window.location.href = "/dashboard"; }}
            sx={{
              textTransform: "none", borderRadius: 2, fontWeight: 700, fontSize: 14,
              background: paymentResult.success ? "linear-gradient(135deg, #16a34a, #22c55e)" : "linear-gradient(135deg, #dc2626, #b91c1c)",
              boxShadow: paymentResult.success ? "0 4px 14px rgba(22,163,74,0.35)" : "0 4px 14px rgba(220,38,38,0.35)"
            }}>
            {paymentResult.success ? "Back to Dashboard" : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}